// Story 2.10 — TanStack Query fetcher for GET /api/v1/shop/drops/:dropId.
//
// The BuyDossierPage needs the drop's portfolio title + price + kind +
// status to render the confirm modal contextually. This hook calls Story
// 2.8's existing public endpoint (no auth required for read; auth is only
// required for the eligibility endpoint which we DON'T need here because
// the transactional purchase POST is the source of truth for cap + balance
// state at the moment the user clicks Buy).
//
// staleTime 30s + no retry to match the established wallet-balance posture.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** Roster entry as the public drop-detail endpoint serves it. Carries no
 *  odds: creator selection is uniform, and certificate-tier odds are a
 *  property of the dossier (see DropForPurchase.tier_distribution). */
export type DropForPurchaseCreator = {
  user_id: string;
  display_name: string;
  handle: string;
  image_signed_url: string | null;
  hello_text: string | null;
  optional_drop_specific_hello_text: string | null;
  position_in_portfolio: number;
};

/** Certificate-tier probabilities as fractions summing to 1.0, ALREADY
 *  RESOLVED server-side: the dossier's own distribution, or the platform
 *  default when it has none. Non-optional on purpose — a buyer-facing
 *  surface should never have to decide what a null means, because ToS §6
 *  promises the published probabilities are the ones the draw uses. */
export type TierDistributionPercents = {
  common: number;
  preferred: number;
  premium: number;
  founders: number;
};

export type DropForPurchase = {
  drop_id: string;
  portfolio_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  portfolio_description: string | null;
  kind: "regular" | "special" | "welcome";
  status: "scheduled" | "live" | "retiring" | "retired";
  price_cents: number;
  per_user_cap: number | null;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  retired_at: string | null;
  is_perpetual: boolean;
  creator_count: number;
  creators: DropForPurchaseCreator[];
  tier_distribution: TierDistributionPercents;
  linked_bulletin_post_id: string | null;
};

async function fetchDropForPurchase(dropId: string): Promise<DropForPurchase> {
  const response = await fetch(`/api/v1/shop/drops/${dropId}`, {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Drop fetch failed (HTTP ${response.status})`);
  }
  const body = (await response.json()) as { data: DropForPurchase };
  return body.data;
}

export function useDropForPurchase(dropId: string) {
  return useQuery({
    queryKey: ["drop-for-purchase", dropId],
    queryFn: () => fetchDropForPurchase(dropId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
    // Guard the empty-id case (Shop hero fetches the highlighted drop's
    // detail only once a drop is selected) so we never hit
    // `/api/v1/shop/drops/` with a trailing-slash 404.
    enabled: dropId.length > 0,
  });
}
